import { useState, useMemo, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FlaskConical,
  Mail,
  Lock,
  ArrowRight,
  ArrowLeft,
  User,
  GraduationCap,
  Microscope,
  Ticket,
  Eye,
  EyeOff,
  Check,
  X,
  Sparkles,
  DollarSign,
  Users,
} from 'lucide-react';
import { registerSchema } from '@apex/shared';
import { useAuthStore } from '@/lib/store';

/* ─── animation variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const cardVariant = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

/* ─── password requirements ─── */
interface PwdReq {
  label: string;
  test: (v: string) => boolean;
}

const pwdRequirements: PwdReq[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One number', test: (v) => /[0-9]/.test(v) },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuthStore();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState<'student' | 'researcher' | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* password strength */
  const pwdMet = useMemo(() => pwdRequirements.map((r) => r.test(password)), [password]);
  const pwdScore = pwdMet.filter(Boolean).length;
  const strengthColor =
    pwdScore <= 1 ? 'bg-danger-500' : pwdScore <= 2 ? 'bg-warning-500' : pwdScore <= 3 ? 'bg-brand-500' : 'bg-success-500';
  const strengthLabel =
    pwdScore <= 1 ? 'Weak' : pwdScore <= 2 ? 'Fair' : pwdScore <= 3 ? 'Good' : 'Strong';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!role) {
      toast.error('Please select your role');
      return;
    }

    const payload = {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      role,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
    };

    /* Frontend validation with zod */
    const result = registerSchema.safeParse(payload);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!errors[key]) errors[key] = issue.message;
      });
      setFieldErrors(errors);
      const firstMsg = result.error.issues[0]?.message;
      toast.error(firstMsg || 'Please fix the errors below');
      return;
    }

    setIsSubmitting(true);
    try {
      await register(payload);
      toast.success('Account created! Let\'s complete your profile.');
      navigate('/profile');
    } catch (err: any) {
      toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex overflow-hidden">
      {/* ───── Background ───── */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-brand-600/20 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-accent-600/15 via-transparent to-transparent" />
      <div className="absolute top-32 left-[10%] w-80 h-80 bg-brand-500/10 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-20 right-[15%] w-72 h-72 bg-accent-500/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }}
      />

      {/* ───── Left Hero ───── */}
      <div className="hidden lg:flex flex-1 relative flex-col justify-center px-12 xl:px-20">
        <motion.div initial="hidden" animate="visible" className="max-w-lg">
          <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center shadow-2xl shadow-brand-500/30">
              <FlaskConical className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">APEX</h1>
              <p className="text-xs text-gray-400 font-medium tracking-widest uppercase">Research Marketplace</p>
            </div>
          </motion.div>

          <motion.h2 variants={fadeUp} custom={1} className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-6">
            Join the Future of{' '}
            <span className="gradient-text">Research Participation.</span>
          </motion.h2>

          <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-400 leading-relaxed mb-12">
            Whether you're a student looking to earn money or a researcher seeking reliable participants — APEX has you covered.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="grid grid-cols-2 gap-4">
            {[
              { num: '500+', label: 'Active Participants' },
              { num: '120+', label: 'Studies Published' },
              { num: '$45K', label: 'Paid to Students' },
              { num: '4.8/5', label: 'Avg Reliability' },
            ].map((stat) => (
              <div key={stat.label} className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
                <p className="text-2xl font-bold text-white">{stat.num}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* ───── Right Registration Card ───── */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-8 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key="register"
            variants={cardVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="w-full max-w-md"
          >
            {/* Mobile brand */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center shadow-lg shadow-brand-500/25">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-white tracking-tight">APEX</h1>
            </div>

            {/* Glass card */}
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-2xl shadow-black/30 p-8 sm:p-10">
              <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" />

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Create your account</h2>
                <p className="text-sm text-gray-400">Get started with APEX in under a minute</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-300">First name</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                      <input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="input-field pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30 text-sm"
                      />
                    </div>
                    {fieldErrors.first_name && (
                      <p className="text-xs text-danger-400">{fieldErrors.first_name}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-300">Last name</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                      <input
                        id="lastName"
                        type="text"
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="input-field pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30 text-sm"
                      />
                    </div>
                    {fieldErrors.last_name && (
                      <p className="text-xs text-danger-400">{fieldErrors.last_name}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">UCI Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@uci.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30 text-sm"
                    />
                  </div>
                  {fieldErrors.email ? (
                    <p className="text-xs text-danger-400">{fieldErrors.email}</p>
                  ) : (
                    <p className="text-xs text-gray-600">Must be a valid @uci.edu email</p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field pl-10 pr-12 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="text-xs text-danger-400">{fieldErrors.password}</p>
                  )}

                  {/* Strength indicator */}
                  {password.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-2 pt-1"
                    >
                      {/* Bar */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full ${strengthColor}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${(pwdScore / 4) * 100}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          pwdScore <= 1 ? 'text-danger-400' : pwdScore <= 2 ? 'text-warning-400' : pwdScore <= 3 ? 'text-brand-400' : 'text-success-400'
                        }`}>{strengthLabel}</span>
                      </div>
                      {/* Requirements */}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        {pwdRequirements.map((req, i) => (
                          <div key={req.label} className="flex items-center gap-1.5">
                            {pwdMet[i] ? (
                              <Check className="w-3 h-3 text-success-400 flex-shrink-0" />
                            ) : (
                              <X className="w-3 h-3 text-gray-600 flex-shrink-0" />
                            )}
                            <span className={`text-[11px] ${pwdMet[i] ? 'text-gray-400' : 'text-gray-600'}`}>
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Role selector */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">I am a...</label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Student */}
                    <button
                      type="button"
                      onClick={() => setRole('student')}
                      className={`relative group p-4 rounded-2xl border text-left transition-all duration-300 ${
                        role === 'student'
                          ? 'border-brand-500/60 bg-brand-500/10 shadow-lg shadow-brand-500/10'
                          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                      }`}
                    >
                      {role === 'student' && (
                        <motion.div
                          layoutId="roleIndicator"
                          className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2.5 transition-colors duration-300 ${
                        role === 'student' ? 'bg-brand-500/20' : 'bg-white/5'
                      }`}>
                        <GraduationCap className={`w-5 h-5 ${role === 'student' ? 'text-brand-400' : 'text-gray-500'}`} />
                      </div>
                      <p className="text-sm font-semibold text-white">Student</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                        Earn money participating in research studies
                      </p>
                    </button>

                    {/* Researcher */}
                    <button
                      type="button"
                      onClick={() => setRole('researcher')}
                      className={`relative group p-4 rounded-2xl border text-left transition-all duration-300 ${
                        role === 'researcher'
                          ? 'border-accent-500/60 bg-accent-500/10 shadow-lg shadow-accent-500/10'
                          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                      }`}
                    >
                      {role === 'researcher' && (
                        <motion.div
                          layoutId="roleIndicator"
                          className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2.5 transition-colors duration-300 ${
                        role === 'researcher' ? 'bg-accent-500/20' : 'bg-white/5'
                      }`}>
                        <Microscope className={`w-5 h-5 ${role === 'researcher' ? 'text-accent-400' : 'text-gray-500'}`} />
                      </div>
                      <p className="text-sm font-semibold text-white">Researcher</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                        Find reliable, verified participants fast
                      </p>
                    </button>
                  </div>
                  {fieldErrors.role && (
                    <p className="text-xs text-danger-400">{fieldErrors.role}</p>
                  )}
                </div>

                {/* Invite code toggle */}
                <div>
                  {!showInvite ? (
                    <button
                      type="button"
                      onClick={() => setShowInvite(true)}
                      className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-400 transition-colors"
                    >
                      <Ticket className="w-3.5 h-3.5" />
                      Have an invite code?
                    </button>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-1.5"
                    >
                      <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-300">
                        Invite Code
                        <span className="text-gray-600 font-normal ml-1">(optional)</span>
                      </label>
                      <div className="relative">
                        <Ticket className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        <input
                          id="inviteCode"
                          type="text"
                          placeholder="Enter invite code"
                          value={inviteCode}
                          onChange={(e) => setInviteCode(e.target.value)}
                          className="input-field pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30 text-sm"
                        />
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-base mt-2"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Creating account...</span>
                    </div>
                  ) : (
                    <>
                      Create Account
                      <Sparkles className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Login link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="font-semibold text-brand-400 hover:text-brand-300 transition-colors">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-600 mt-6">
              By creating an account, you agree to APEX's Terms of Service and Privacy Policy.
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
